import React, { FC, useState } from 'react';
import { Button, Col, Descriptions, Input, Row, Table, Tag, Typography } from 'antd';
import FileSaver from 'file-saver';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';

import { GauntletLineupPlayerDto, GetGauntletLineupResponseDto } from '@services/_api';
import { getGauntletLineup } from '@services/cstool/recovery-account';

import ClientVersionSearchSelect from './client-version-search-select';
import { useGetCurrentProgressContext } from './context';

const { Title } = Typography;

type Status = 'success' | 'no-result' | 'error';

const GauntletLineupSearch: FC = () => {
  const { environment } = useGetCurrentProgressContext();
  const [userId, setUserId] = useState<string>();
  const [roundId, setRoundId] = useState<string>();
  const [clientVersion, setClientVersion] = useState<string>();
  const [data, setData] = useState<GetGauntletLineupResponseDto>();
  const [status, setStatus] = useState<Status>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);

  const handleSearch = async () => {
    if (!userId || !roundId || !environment || !clientVersion) return;
    setIsFetching(true);
    setData(undefined);
    setStatus(undefined);
    setErrorMessage(undefined);
    const response = await getGauntletLineup({
      userId,
      roundId,
      environment,
      clientVersion,
    }).finally(() => {
      setIsFetching(false);
    });
    if (response.ok) {
      if (response.data) {
        setData(response.data);
        setStatus('success');
      } else {
        setStatus('no-result');
      }
    } else {
      setStatus('error');
      setErrorMessage(response.error?.message);
    }
  };

  const handleDownloadPlayer = (player: GauntletLineupPlayerDto) => {
    if (!player.lineup || !roundId) return;
    const blob = new Blob([JSON.stringify(player.lineup, null, 2)], {
      type: 'text/plain;charset=utf-8',
    });
    FileSaver.saveAs(blob, `gauntlet-lineup-${player.userId}-round-${roundId}.txt`);
  };

  const handleDownloadAll = () => {
    if (!data) return;
    data.players.filter((player) => player.lineup).forEach(handleDownloadPlayer);
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: 'Match Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: number | null) => (value === null || value === undefined ? '-' : value),
    },
    {
      title: 'Finished',
      dataIndex: 'finished',
      key: 'finished',
      render: (finished: boolean) =>
        finished ? <Tag color="green">Finished</Tag> : <Tag color="orange">Not Finished</Tag>,
    },
    {
      title: 'Lineup',
      key: 'lineup',
      render: (_: unknown, player: GauntletLineupPlayerDto) =>
        player.lineup ? (
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPlayer(player)}
          >
            Download
          </Button>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className="flex flex-col">
      <Row gutter={[16, 16]} className="mb-4">
        <Col>
          <Title level={5} className="mr-2">
            User ID
          </Title>
          <Input
            value={userId}
            placeholder="Enter user ID"
            style={{ width: 250 }}
            onChange={(e) => setUserId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            Round ID
          </Title>
          <Input
            value={roundId}
            placeholder="Enter round ID"
            style={{ width: 150 }}
            onChange={(e) => setRoundId(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col>
          <Title level={5} className="mr-2">
            Client Version
          </Title>
          <ClientVersionSearchSelect
            environment={environment}
            value={clientVersion}
            style={{ width: 200 }}
            onChange={setClientVersion}
            disabled={!environment}
          />
        </Col>
        <Col className="flex items-end">
          <Button
            htmlType="submit"
            type="primary"
            loading={isFetching}
            onClick={handleSearch}
            disabled={!userId || !roundId || !environment || !clientVersion}
          >
            <SearchOutlined />
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]} align="middle" className="mb-4">
        <Col>
          {status === 'success' && <Tag color="green">Success</Tag>}
          {status === 'no-result' && <Tag color="orange">No Result</Tag>}
          {status === 'error' && <Tag color="red">{`Error : ${errorMessage}`}</Tag>}
        </Col>
        {status === 'success' && (
          <Col>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadAll}>
              Download All Lineups
            </Button>
          </Col>
        )}
      </Row>
      {status === 'success' && data && (
        <>
          <Descriptions size="small" column={4} className="mb-4">
            <Descriptions.Item label="Season ID">{data.seasonId}</Descriptions.Item>
            <Descriptions.Item label="Round ID">{data.roundId}</Descriptions.Item>
            <Descriptions.Item label="Matchup ID">{data.matchupId}</Descriptions.Item>
            <Descriptions.Item label="Random Seed">{data.randomSeed ?? '-'}</Descriptions.Item>
          </Descriptions>
          <Table
            size="small"
            rowKey="userId"
            columns={columns}
            dataSource={data.players}
            pagination={false}
          />
        </>
      )}
    </div>
  );
};

export default GauntletLineupSearch;
